import React, { useEffect, useState } from 'react';
import { Trophy, Award, MapPin, Star, Info, ChevronRight, Sparkles } from 'lucide-react';
import { useAchievement } from '../../context/AchievementContext';
import AnimatedCounter from '../../components/ui/AnimatedCounter';
import Badge from '../../components/ui/Badge';
import ConfettiButton from '../../components/ui/ConfettiButton';
const FoodPassport: React.FC = () => {
  const {
    badges,
    achievements,
    stamps,
    points,
    level
  } = useAchievement();
  const [showAnimation, setShowAnimation] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<string | null>(null);
  useEffect(() => {
    setShowAnimation(true);
    const timer = setTimeout(() => setShowAnimation(false), 1500);
    return () => clearTimeout(timer);
  }, []);
  const handleBadgeClick = (badgeId: string) => {
    setSelectedBadge(selectedBadge === badgeId ? null : badgeId);
  };
  return <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1020">
      {/* Header with stats */}
      <div className="bg-gradient-to-r from-[#E84118] to-[#c83616] rounded-lg p-6 md:p-8 text-white mb-6 md:mb-8" data-id="element-1021">
        <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-1022">
          <div className="text-center md:text-left mb-4 md:mb-0" data-id="element-1023">
            <h1 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2 flex items-center" data-id="element-1024">
              Food Passport
              <Sparkles className="ml-2 animate-pulse" size={20} data-id="element-1025" />
            </h1>
            <p className="text-base md:text-lg opacity-90" data-id="element-1026">
              Level {level} Explorer
            </p>
          </div>
          <div className="flex items-center bg-white bg-opacity-20 rounded-lg p-4 transform hover:scale-105 transition-transform duration-300" data-id="element-1027">
            <div className="text-center" data-id="element-1028">
              <div className={`text-3xl md:text-4xl font-bold ${showAnimation ? 'animate-bounce' : ''}`} data-id="element-1029">
                <AnimatedCounter value={points} duration={1500} data-id="element-1030" />
              </div>
              <div className="text-xs md:text-sm opacity-90" data-id="element-1031">Total Points</div>
            </div>
            <div className="ml-6 pl-6 border-l border-white border-opacity-30" data-id="element-1032">
              <div className="text-3xl md:text-4xl font-bold" data-id="element-1033">
                <AnimatedCounter value={level} duration={1000} data-id="element-1034" />
              </div>
              <div className="text-xs md:text-sm opacity-90" data-id="element-1035">Level</div>
            </div>
          </div>
        </div>
      </div>
      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 md:mb-8 rounded-r-md transform hover:translate-y-[-2px] transition-transform duration-200" data-id="element-1036">
        <div className="flex" data-id="element-1037">
          <Info className="text-blue-500 mr-3 flex-shrink-0 mt-0.5" size={20} data-id="element-1038" />
          <div data-id="element-1039">
            <p className="font-medium text-blue-700 mb-1" data-id="element-1040">
              Collect points with every order!
            </p>
            <p className="text-sm text-blue-600" data-id="element-1041">
              Earn stamps for different cuisines and restaurants. Level up to
              unlock exclusive rewards and discounts.
            </p>
          </div>
        </div>
      </div>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8" data-id="element-1042">
        <div className="bg-white rounded-lg shadow-md p-5 transform hover:shadow-lg transition-all duration-300" data-id="element-1043">
          <div className="flex items-center justify-between mb-3" data-id="element-1044">
            <h3 className="font-medium text-gray-700" data-id="element-1045">Stamps Collected</h3>
            <MapPin className="text-[#E84118]" size={20} data-id="element-1046" />
          </div>
          <div className="text-2xl md:text-3xl font-bold" data-id="element-1047">
            <AnimatedCounter value={stamps.length} duration={1200} data-id="element-1048" />
          </div>
          <div className="mt-2 text-xs text-gray-500" data-id="element-1049">
            From {stamps.length} different restaurants
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-5 transform hover:shadow-lg transition-all duration-300" data-id="element-1050">
          <div className="flex items-center justify-between mb-3" data-id="element-1051">
            <h3 className="font-medium text-gray-700" data-id="element-1052">Badges Earned</h3>
            <Award className="text-[#E84118]" size={20} data-id="element-1053" />
          </div>
          <div className="text-2xl md:text-3xl font-bold" data-id="element-1054">
            <AnimatedCounter value={badges.filter(b => b.unlocked).length} duration={1200} data-id="element-1055" />
            <span className="text-sm font-normal text-gray-500" data-id="element-1056">
              /{badges.length}
            </span>
          </div>
          <div className="mt-2 text-xs text-gray-500" data-id="element-1057">
            Keep exploring to earn more!
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-5 col-span-2 md:col-span-1 transform hover:shadow-lg transition-all duration-300" data-id="element-1058">
          <div className="flex items-center justify-between mb-3" data-id="element-1059">
            <h3 className="font-medium text-gray-700" data-id="element-1060">Next Level</h3>
            <Trophy className="text-[#E84118]" size={20} data-id="element-1061" />
          </div>
          <div className="text-lg font-bold flex items-center" data-id="element-1062">
            <span data-id="element-1063">Level {level}</span>
            <ChevronRight className="mx-1 text-gray-400" size={16} data-id="element-1064" />
            <span data-id="element-1065">Level {level + 1}</span>
          </div>
          <div className="mt-2" data-id="element-1066">
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-1067">
              <div className="bg-[#E84118] h-2 rounded-full transition-all duration-1000 ease-out" style={{
              width: showAnimation ? `${points % 1000 / 10}%` : '0%'
            }} data-id="element-1068" />
            </div>
            <div className="mt-1 text-xs text-gray-500 flex justify-between" data-id="element-1069">
              <span data-id="element-1070">{points % 1000} points</span>
              <span data-id="element-1071">{1000 - points % 1000} more needed</span>
            </div>
          </div>
        </div>
      </div>
      {/* Badges */}
      <div className="bg-white rounded-lg shadow-md p-5 md:p-6 mb-6 md:mb-8" data-id="element-1072">
        <h2 className="text-lg md:text-xl font-bold mb-5 md:mb-6 flex items-center" data-id="element-1073">
          Badges
          <Badge variant="info" size="sm" className="ml-2" data-id="element-1074">
            Collect them all!
          </Badge>
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-1075">
          {badges.map(badge => <div key={badge.id} className={`p-4 rounded-lg text-center transition-all duration-300 cursor-pointer transform ${selectedBadge === badge.id ? 'scale-105' : ''} ${badge.unlocked ? 'bg-green-50 border border-green-100 hover:shadow-md' : 'bg-gray-50 border border-gray-100 opacity-75'}`} onClick={() => handleBadgeClick(badge.id)} data-id="element-1076">
              <div className={`w-14 h-14 mx-auto mb-3 rounded-full bg-white flex items-center justify-center border border-gray-100 shadow-sm ${badge.unlocked && selectedBadge === badge.id ? 'animate-pulse' : ''}`} data-id="element-1077">
                <Star className={badge.unlocked ? 'text-[#E84118]' : 'text-gray-300'} data-id="element-1078" />
              </div>
              <h3 className="font-medium mb-1 text-sm" data-id="element-1079">{badge.name}</h3>
              {selectedBadge === badge.id && <p className="text-xs text-gray-600 mt-2" data-id="element-1080">
                  {badge.description}
                </p>}
              {!selectedBadge === badge.id && <p className="text-xs text-gray-600 line-clamp-1" data-id="element-1081">
                  {badge.description}
                </p>}
            </div>)}
        </div>
      </div>
      {/* Recent Stamps */}
      <div className="bg-white rounded-lg shadow-md p-5 md:p-6" data-id="element-1082">
        <h2 className="text-lg md:text-xl font-bold mb-5 md:mb-6" data-id="element-1083">
          Recent Stamps
        </h2>
        {stamps.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100" data-id="element-1084">
            <MapPin className="mx-auto text-gray-300 mb-3" size={32} data-id="element-1085" />
            <p className="text-gray-500 mb-1" data-id="element-1086">
              Start ordering to collect stamps!
            </p>
            <p className="text-xs text-gray-400 mb-4" data-id="element-1087">
              Each cuisine and restaurant adds a new stamp to your passport
            </p>
            <ConfettiButton variant="primary" size="md" data-id="element-1088">
              Place Your First Order
            </ConfettiButton>
          </div> : <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1089">
            {stamps.map((stamp, index) => <div key={stamp.id} className="border rounded-lg p-4 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1" style={{
          animationDelay: `${index * 0.1}s`,
          opacity: showAnimation ? 0 : 1,
          animation: showAnimation ? `fadeInUp 0.5s ${index * 0.1}s forwards` : 'none'
        }} data-id="element-1090">
                <div className="flex items-center mb-3" data-id="element-1091">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mr-3" data-id="element-1092">
                    <MapPin className="text-[#E84118]" size={16} data-id="element-1093" />
                  </div>
                  <div data-id="element-1094">
                    <h3 className="font-medium text-sm" data-id="element-1095">{stamp.cuisine}</h3>
                    <p className="text-xs text-gray-500" data-id="element-1096">{stamp.restaurant}</p>
                  </div>
                </div>
                <div className="text-xs text-gray-500 flex justify-between items-center pt-2 border-t border-gray-100" data-id="element-1097">
                  <span className="text-green-600 font-medium" data-id="element-1098">+50 points</span>
                  <span data-id="element-1099">{stamp.date.toLocaleDateString()}</span>
                </div>
              </div>)}
          </div>}
      </div>
      {/* CSS for animations */}
      <style jsx data-id="element-1100">{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>;
};
export default FoodPassport;